import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { EventoRelacionado } from '../../componentes/chatter';
import { get } from '../../nucleo/api';
import { nomeDeArquivo, textoLivre } from '../../nucleo/formato';
import { textoVinculo, type VinculoEstruturado } from '../../nucleo/vinculos';
import { caminhoDaNegociacaoCrm, caminhoDoAgendamento } from './crm-comum';

/** Review rlb-1 (SPEC-B1): registros por leitura do histórico agregado (o teto do servidor, `TETO_REGISTROS_AGREGADO`). */
export const TETO_REGISTROS_HISTORICO = 60;

/** Um de cada grupo por vez, na ordem de cada grupo (função pura, testada). */
export function intercalarHistorico<T>(grupos: T[][]): T[] {
  const saida: T[] = [];
  for (let i = 0; grupos.some((g) => i < g.length); i++) for (const g of grupos) if (i < g.length) saida.push(g[i]!);
  return saida;
}

/** Os registros ligados ao parceiro que entram no histórico: contatos, negociações e o que outros módulos ligam (tarefas, reuniões). */
export interface RegistroDoHistorico {
  entidade: string;
  id: string;
  origem: ReactNode;
}

/**
 * Lista de 30/10 (B), item 3.1 (a), e rodada rrl-2 (SPEC-M1): o histórico do parceiro com o que o envolve no sistema, na ficha da
 * empresa e na do contato. `proprios` são os grupos que a ficha já conhece; os de `GET /cadastros/parceiros/:id/vinculos` entram
 * depois deles. A leitura é do `GET /chatter-agregado`, que confere de novo permissão, empresa e a regra de cada módulo.
 */
export function useHistoricoDoParceiro({ chave, parceiroId, nomes, proprios = [] }: { chave: readonly unknown[]; parceiroId: string; nomes: (string | null | undefined)[]; proprios?: RegistroDoHistorico[][] }) {
  const { t } = useTranslation('cadastros');
  const vinculos = useQuery({
    queryKey: [...chave, 'vinculos'],
    queryFn: () => get<{ modulo: string; itens: { id: string; rotulo: string; link: string; entidade?: string; estruturado?: VinculoEstruturado }[] }[]>(`/cadastros/parceiros/${parceiroId}/vinculos`),
  });
  const textosDosNomes = nomes.filter((n): n is string => !!n);
  const jaEstao = proprios.flat();
  const dosVinculos: RegistroDoHistorico[] = [];
  const rotuloDaOrigem = (entidade: string, rotulo: string) =>
    entidade === 'tarefas.card'
      ? t('crm.lbEmpresa.origem.tarefa', { nome: rotulo })
      : entidade === 'agenda.agendamento'
        ? t('crm.lbEmpresa.origem.reuniao', { nome: rotulo })
        : entidade === 'pipeline.oportunidade'
          ? t('crm.lbEmpresa.origem.negociacao', { nome: rotulo })
          : t('crm.lbEmpresa.origem.outro', { nome: rotulo });
  // Rodada rrl-3 (RRL3-VIS-B1, SPEC-B1): o histórico é do CRM; negociação e reunião abrem no CRM, não no caminho que o módulo publica.
  const linkDaOrigem = (entidade: string, id: string, link: string) => (entidade === 'pipeline.oportunidade' ? caminhoDaNegociacaoCrm(id) : entidade === 'agenda.agendamento' ? caminhoDoAgendamento(id) : link);
  for (const g of vinculos.data ?? []) {
    for (const i of g.itens) {
      if (!i.entidade || [...jaEstao, ...dosVinculos].some((r) => r.entidade === i.entidade && r.id === i.id)) continue;
      // Rodada rrl-3 (RRL3-SEG-M1, RRL3-VIS-M1): pelo `textoVinculo`, a pessoa da reunião sai por `nomePessoa` e o "com" no idioma do app.
      dosVinculos.push({ entidade: i.entidade, id: i.id, origem: <OrigemDoHistorico texto={rotuloDaOrigem(i.entidade, textoLivre(textoVinculo(g.modulo, i).rotulo, ...textosDosNomes))} link={linkDaOrigem(i.entidade, i.id, i.link)} /> });
    }
  }
  // Review rlb-1 (SPEC-B1): o teto de registros pega um de cada grupo por vez (negociação, contato, tarefa ou reunião), para
  // nenhum grupo sumir inteiro, e a tela avisa quando corta.
  const registros = intercalarHistorico([...proprios, dosVinculos]);
  const lista = registros.slice(0, TETO_REGISTROS_HISTORICO);
  const registrosNaChave = lista.map((r) => `${r.entidade}:${r.id}`).join(',');
  const agregado = useQuery({
    queryKey: [...chave, 'historico', registrosNaChave],
    queryFn: () =>
      get<{
        mensagens: (Omit<EventoRelacionado, 'origem' | 'conteudo'> & { registro_id: string })[];
        anexos: { id: string; nome: string; created_at: string; autor_nome: string | null; entidade: string; registro_id: string }[];
        truncado?: boolean;
      }>('/chatter-agregado', { registros: registrosNaChave }),
    enabled: !!registrosNaChave && !vinculos.isPending,
  });
  const origemDe = (entidade: string, id: string) => lista.find((r) => r.entidade === entidade && r.id === id)?.origem ?? null;
  const relacionados: EventoRelacionado[] = [
    ...(agregado.data?.mensagens ?? []).map((m) => ({ ...m, origem: origemDe(m.entidade, m.registro_id) })),
    // Os arquivos dos registros ligados entram como evento, na data do envio, com o link de baixar.
    ...(agregado.data?.anexos ?? []).map(
      (a): EventoRelacionado => ({
        id: `anexo-${a.id}`,
        tipo: 'sistema',
        corpo: '',
        alteracoes: null,
        parametros: null,
        created_at: a.created_at,
        autor_nome: a.autor_nome,
        entidade: a.entidade,
        origem: origemDe(a.entidade, a.registro_id),
        conteudo: (
          <>
            {t('crm.lbEmpresa.anexou')}{' '}
            <a href={`/api/v1/anexos/${a.id}`} className="text-azul hover:underline" title={nomeDeArquivo(a.nome, ...textosDosNomes)}>
              {nomeDeArquivo(a.nome, ...textosDosNomes)}
            </a>
          </>
        ),
      }),
    ),
  ];
  const cortado = registros.length > lista.length ? t('crm.lbEmpresa.historicoCortadoRegistros', { n: lista.length, total: registros.length }) : agregado.data?.truncado ? t('crm.lbEmpresa.historicoCortadoEventos', { n: agregado.data.mensagens.length }) : null;
  return { relacionados, cortado };
}

export function OrigemDoHistorico({ texto, link }: { texto: string; link: string }) {
  return (
    <Link to={link} className="text-azul hover:underline">
      {texto}
    </Link>
  );
}
